/* eslint-disable no-restricted-imports */
import { useState } from 'react';

import {
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp, Search as SearchIcon } from '@material-ui/icons';

import { FormatDate, FormatMoney } from 'components';
import { StatusChip } from 'components/Contigo';

function OrderRow({ order, classes }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size='small' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{order.order_number}</TableCell>
        <TableCell>{order.customer_name}</TableCell>
        <TableCell>
          <StatusChip status={order.status} size='small' />
        </TableCell>
        <TableCell>
          <FormatDate>{order.created_at}</FormatDate>
        </TableCell>
        <TableCell align='center'>{order.total_items}</TableCell>
        <TableCell align='center'>
          {order.total_fulfilled}/{order.total_quantity}
        </TableCell>
        <TableCell align='center'>
          {order.total_invoiced}/{order.total_fulfilled}
        </TableCell>
        <TableCell align='right'>
          <FormatMoney>{order.total_value}</FormatMoney>
        </TableCell>
        <TableCell align='right'>
          <Typography
            className={order.profit >= 0 ? classes.profitPositive : classes.profitNegative}
          >
            <FormatMoney>{order.profit}</FormatMoney>
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box margin={2}>
              <Typography variant='h6' gutterBottom>
                Itens do Pedido
              </Typography>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Part Number</TableCell>
                    <TableCell>PN Anterior</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align='center'>Pedido</TableCell>
                    <TableCell align='center'>Atendido</TableCell>
                    <TableCell align='center'>Faturado</TableCell>
                    <TableCell align='right'>Venda Un.</TableCell>
                    <TableCell align='right'>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.part_number}</TableCell>
                      <TableCell>
                        {item.previous_part_number ? (
                          <Chip
                            label={item.previous_part_number}
                            size='small'
                            style={{ backgroundColor: '#FFF9C4', color: '#F57F17' }}
                          />
                        ) : (
                          <Typography variant='caption' color='textSecondary'>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell align='center'>
                        <Chip label={item.quantity} size='small' color='primary' />
                      </TableCell>
                      <TableCell align='center'>
                        <Chip
                          label={item.quantity_fulfilled}
                          size='small'
                          style={{ backgroundColor: '#F3E5F5', color: '#7B1FA2' }}
                        />
                      </TableCell>
                      <TableCell align='center'>
                        <Chip
                          label={item.quantity_invoiced}
                          size='small'
                          style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <FormatMoney>{item.sell_price}</FormatMoney>
                      </TableCell>
                      <TableCell align='right'>
                        <FormatMoney>{item.total_value}</FormatMoney>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function DetailedTab({ data, classes }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredOrders = data.detailed.filter((order) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      order.order_number.toLowerCase().includes(searchLower) ||
      order.customer_name.toLowerCase().includes(searchLower) ||
      order.customer_cgc.includes(searchLower) ||
      (order.qqn_number && order.qqn_number.toLowerCase().includes(searchLower)) ||
      (order.samsung_order_number && order.samsung_order_number.toLowerCase().includes(searchLower))
    );
  });

  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box mb={2}>
        <TextField
          fullWidth
          variant='outlined'
          size='small'
          placeholder='Pesquisar por número do pedido, cliente, CPF/CNPJ, Q&N ou pedido Samsung...'
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Nº Pedido</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align='center'>Itens</TableCell>
              <TableCell align='center'>Atendimento</TableCell>
              <TableCell align='center'>Faturamento</TableCell>
              <TableCell align='right'>Valor Total</TableCell>
              <TableCell align='right'>Lucro</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrders.length > 0 ? (
              paginatedOrders.map((order) => (
                <OrderRow key={order.order_id} order={order} classes={classes} />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  <Typography variant='body2' color='textSecondary'>
                    Nenhum pedido encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component='div'
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage='Linhas por página:'
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
    </Box>
  );
}
